// TODO - remove lint disable when editing this file
/* eslint-disable */
import { processInlineComments } from '../../../util/inlineComments'
import {
  ContentState,
  ContentBlock,
  SelectionState,
  Modifier,
  CharacterMetadata,
} from 'draft-js'
import { OrderedSet, List } from 'immutable'

const inlineStyleSet = OrderedSet(['INLINECOMMENT'])

export const _inlineCommentReducer = (contentState, comment) => {
  const selection = SelectionState.createEmpty(
    contentState.getFirstBlock().getKey()
  ).merge({
    anchorOffset: comment.pos,
    focusOffset: comment.pos,
  })

  return Modifier.insertText(
    contentState,
    selection,
    comment.comment,
    inlineStyleSet
  )
}

const processInlineCommentsWithReducer = processInlineComments(
  _inlineCommentReducer
)

const containsValidComments = (ans) => {
  if (!ans) {
    return false
  }

  const {
    additional_properties: { inline_comments: inlineComments = [] } = {},
  } = ans

  return Array.isArray(inlineComments) && inlineComments.length > 0
}

/**
 * inlineCommentsSpacesMiddleware receive raw HTML as a string and ANS ContentBlock
 * if there are any inline comments, all spaces have to be converted to non-breakable spaces
 * because:
 * 1 - Draft compress multiple spaces into one
 * 2 - comments rely on the absolute position in the text
 * having all spaces as non-breakable spaces does not require additional processing since
 * DraftJS will convert it into regular space (x20)
 */

export const inlineCommentsConvertSpacesMiddleware = (html, ans, options) => {
  if (!containsValidComments(ans)) {
    return html
  }
  const withNbsps = (html || '').replace(/\s/g, '&nbsp;')
  // Wrap in span to preserve leading spaces
  return `<span>${withNbsps}</span>`
}

/**
 * inlineCommentsImportMiddleware receive ANS ContentBlock and
 * insert inline comments as styled text
 */

export const inlineCommentsImportMiddleware = (data, ans) => {
  if (!containsValidComments(ans)) {
    return data
  }
  if (data.contentBlocks.length !== 1) {
    console.warn(ans)
    throw new RangeError(
      `Unable to edit story due to invalid elements in the ANS element ${
        ans._id
      }. Please notify Arc Support to resolve.`
    )
  }

  const contentState = ContentState.createFromBlockArray(
    data.contentBlocks,
    data.entityMap
  )
  let {
    additional_properties: { inline_comments: inlineComments },
  } = ans
  inlineComments = inlineComments.filter((comment) => comment.pos !== undefined)
  const contentStateWithInlineComments = processInlineCommentsWithReducer(
    contentState,
    inlineComments
  )
  return {
    contentBlocks: contentStateWithInlineComments.getBlocksAsArray(),
    entityMap: contentStateWithInlineComments.getEntityMap(),
  }
}

// Export middleware logic

class State {
  constructor(state, charIndex) {
    if (!state) {
      this.locationPairs = []
      return
    }

    if (charIndex === undefined) {
      throw new Error('charIndex index can not be empty')
    }
    this.locationPairs = [...state.locationPairs, charIndex]
  }
  transition(isInlineComment, charIndex) {
    return this
  }
  getPairs(textLength) {
    const result = []
    for (let i = 0; i < this.locationPairs.length; i += 2) {
      const [start, end = textLength] = this.locationPairs.slice(i, i + 2)
      result.push([start, end])
    }
    return result
  }
}

class OutState extends State {
  transition(isInlineComment, charIndex) {
    return isInlineComment ? new InState(this, charIndex) : this
  }
}
class InState extends State {
  transition(isInlineComment, charIndex) {
    return isInlineComment ? this : new OutState(this, charIndex)
  }
}

export const _extractInlineComments = (block) => {
  let state = new OutState()
  block.getCharacterList().forEach((characterMetadata, charIndex) => {
    const hasInlineCommentStyle = characterMetadata
      .getStyle()
      .has('INLINECOMMENT')
    state = state.transition(hasInlineCommentStyle, charIndex)
  })
  const locationPairs = state.getPairs(block.getText().length)

  const { blockWithoutComments, inlineComments } = locationPairs.reduce(
    _reduceInlineComments,
    {
      blockWithoutComments: block,
      inlineComments: [],
      delta: 0,
    }
  )

  return {
    block: blockWithoutComments || block,
    inlineComments,
  }
}

export function _reduceInlineComments(acc, [startOffset, endOffset]) {
  startOffset -= acc.delta
  endOffset -= acc.delta
  const characterList = removeFromList(
    acc.blockWithoutComments.getCharacterList(),
    startOffset,
    endOffset
  )
  const text = acc.blockWithoutComments.getText()
  const commentText = text.slice(startOffset, endOffset)
  const modifiedBlock = acc.blockWithoutComments.merge({
    text: text.slice(0, startOffset) + text.slice(endOffset),
    characterList,
  })
  const inlineComments = [
    ...acc.inlineComments,
    {
      pos: startOffset,
      comment: commentText,
    },
  ]
  return {
    blockWithoutComments: modifiedBlock,
    inlineComments,
    delta: acc.delta + commentText.length,
  }
}

function removeFromList(targetList, startOffset, endOffset) {
  if (startOffset === 0) {
    while (startOffset < endOffset) {
      targetList = targetList.shift()
      startOffset++
    }
  } else if (endOffset === targetList.count()) {
    while (endOffset > startOffset) {
      targetList = targetList.pop()
      endOffset--
    }
  } else {
    var head = targetList.slice(0, startOffset)
    var tail = targetList.slice(endOffset)
    targetList = head.concat(tail).toList()
  }
  return targetList
}

export const inlineCommentsExportMiddleware = (contentState, block) => {
  const result = _extractInlineComments(block)
  const blockKey = block.getKey()
  const blockData = result.block
    .getData()
    .setIn(['additionalProperties', 'inline_comments'], result.inlineComments)
  const newBlock = result.block.set('data', blockData)
  return {
    contentState: contentState.set(
      'blockMap',
      contentState.getBlockMap().set(blockKey, newBlock)
    ),
    block: newBlock,
  }
}
